import * as React from 'react'
import { E, pipe, T } from '@packages/prelude'
import * as RD from '@devexperts/remote-data-ts'
import * as domain from '@packages/domain'
import { usePreviewPolling } from './usePreviewPolling'
import { useIndiAudioPreview, type PreviewData } from '~/hooks/useIndiAudioPreview'

export interface UsePreviewSessionArgs {
  productId: number
  config: domain.Api.Config.Type
  initialPreviewData: RD.RemoteData<domain.Error.Type, domain.Api.IAGenPreviewResponse.Type>
}

export interface UsePreviewSessionResult {
  previewData: RD.RemoteData<domain.Error.Type, domain.Api.IAGenPreviewResponse.Type>
  generate: (payload: ReadonlyArray<PreviewData>) => void
  reset: () => void
}

export const usePreviewSession = ({ productId, initialPreviewData }: UsePreviewSessionArgs): UsePreviewSessionResult => {
  const { generatePreview, getStatus } = useIndiAudioPreview(productId)
  const { previewData, start, set, reset } = usePreviewPolling({ getStatus })

  // Seed from a share-link/SSR-provided preview once on mount. Seeding only happens once:
  // a prop change to initialPreviewData after mount is intentionally ignored so it cannot
  // clobber an in-progress poll. To re-seed from new initialPreviewData, remount the component.
  const seededRef = React.useRef(false)
  React.useEffect(() => {
    if (!seededRef.current) {
      seededRef.current = true
      set(initialPreviewData)
    }
  }, [set, initialPreviewData])

  const generate = React.useCallback(
    (payload: ReadonlyArray<PreviewData>) => {
      set(RD.pending)
      void pipe(
        generatePreview(payload),
        T.map(
          E.fold(
            err => set(RD.failure(err)),
            ({ token }) => start(token)
          )
        )
      )()
    },
    [generatePreview, start, set]
  )

  return { previewData, generate, reset }
}
